import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Camera,
  ChevronDown,
  Loader2,
  Map,
  ReceiptText,
  Search,
  ShieldCheck,
  Store,
  Ticket,
  Wrench,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  getWorkspaceAccess,
  saveWorkspaceAccess,
  workspaceAccessQueryKey,
  type WorkspaceAccessMode,
  type WorkspaceAccessProjectMember,
  type WorkspaceAccessRole,
  type WorkspaceAccessRule,
  type WorkspaceKey,
} from "./api";

const WORKSPACE_ICONS: Partial<Record<WorkspaceKey, typeof Map>> = {
  gis: Map,
  facturas: ReceiptText,
  tickets: Ticket,
  captures: Camera,
  caja: Store,
};

const ROLE_ORDER: WorkspaceAccessRole[] = [
  "OWNER",
  "ADMIN",
  "EDITOR",
  "VIEWER",
];

function policySnapshot(
  workspaces: Partial<Record<WorkspaceKey, WorkspaceAccessRule>>
) {
  return JSON.stringify(
    Object.keys(workspaces)
      .sort()
      .map((workspace) => {
        const rule = workspaces[workspace];
        if (!rule) return [workspace, null];
        return [
          workspace,
          {
            ...rule,
            allowed_roles: [...rule.allowed_roles].sort(),
            allowed_user_ids: [...rule.allowed_user_ids].sort((a, b) => a - b),
            denied_roles: [...rule.denied_roles].sort(),
            denied_user_ids: [...rule.denied_user_ids].sort((a, b) => a - b),
          },
        ];
      })
  );
}

function workspaceLabel(
  workspace: WorkspaceKey,
  t: ReturnType<typeof useTranslation>["t"]
) {
  if (["gis", "facturas", "tickets", "captures", "caja"].includes(workspace)) {
    return t(
      `operations.workspace_access_policy.workspaces.${workspace}.title`
    );
  }
  return t(`operations.sections.${workspace.replaceAll("-", "_")}.label`, {
    defaultValue: workspace,
  });
}

export function WorkspaceAccessPolicyPanel({
  projectId,
}: {
  projectId: string;
}) {
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<
    Partial<Record<WorkspaceKey, WorkspaceAccessRule>>
  >({});
  const [openWorkspace, setOpenWorkspace] = useState<WorkspaceKey | null>(
    "gis"
  );
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: workspaceAccessQueryKey(projectId),
    queryFn: () => getWorkspaceAccess(projectId),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

  useEffect(() => {
    setDraft({});
    setOpenWorkspace("gis");
    setSearch("");
  }, [projectId]);

  useEffect(() => {
    if (query.data?.workspaces && Object.keys(draft).length === 0) {
      setDraft(query.data.workspaces);
    }
  }, [draft, query.data?.workspaces]);

  const mutation = useMutation({
    mutationFn: () => saveWorkspaceAccess(projectId, draft),
    onSuccess: (payload) => {
      queryClient.setQueryData(workspaceAccessQueryKey(projectId), payload);
      setDraft(payload.workspaces);
      toast.success(t("operations.workspace_access_policy.saved"));
    },
  });

  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const members = query.data?.available_project_members ?? [];
    if (!needle) return members;
    return members.filter((member) =>
      [member.name, member.email, member.role].some((value) =>
        value.toLocaleLowerCase().includes(needle)
      )
    );
  }, [query.data?.available_project_members, search]);

  if (query.isPending) {
    return <Skeleton className="h-72 w-full rounded-lg" />;
  }
  if (query.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("operations.workspace_access_policy.error_title")}
          </CardTitle>
          <CardDescription>
            {t("operations.workspace_access_policy.error_description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={() => query.refetch()}
          >
            {t("operations.workspace_access_policy.retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }
  const workspaceKeys = query.data?.workspace_keys ?? Object.keys(draft);
  if (
    !query.data?.can_manage ||
    workspaceKeys.length === 0 ||
    !workspaceKeys.every((key) => draft[key])
  )
    return null;

  const hasChanges =
    policySnapshot(draft) !== policySnapshot(query.data.workspaces ?? {});

  const updateRule = (
    workspace: WorkspaceKey,
    patch: Partial<WorkspaceAccessRule>
  ) => {
    setDraft((current) => ({
      ...current,
      [workspace]: { ...current[workspace]!, ...patch },
    }));
  };

  return (
    <Card>
      <CardHeader className="p-4 sm:p-5">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="size-4" aria-hidden="true" />
          {t("operations.workspace_access_policy.title")}
        </CardTitle>
        <CardDescription className="max-w-3xl text-pretty">
          {t("operations.workspace_access_policy.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
        {workspaceKeys.map((workspace) => {
          const rule = draft[workspace]!;
          const Icon = WORKSPACE_ICONS[workspace] ?? Wrench;
          const isOpen = openWorkspace === workspace;
          return (
            <Collapsible
              key={workspace}
              open={isOpen}
              onOpenChange={(open) => setOpenWorkspace(open ? workspace : null)}
              className="overflow-hidden rounded-lg border"
            >
              <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md px-1 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-background">
                      <Icon className="size-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">
                        {workspaceLabel(workspace, t)}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {t(
                          `operations.workspace_access_policy.modes.${rule.mode}.description`
                        )}
                      </span>
                    </span>
                    <ChevronDown
                      className={cn(
                        "size-4 shrink-0 transition-transform",
                        isOpen && "rotate-180"
                      )}
                      aria-hidden="true"
                    />
                  </button>
                </CollapsibleTrigger>
                <Select
                  value={rule.mode}
                  onValueChange={(mode) =>
                    updateRule(workspace, { mode: mode as WorkspaceAccessMode })
                  }
                  disabled={mutation.isPending}
                >
                  <SelectTrigger
                    className="min-h-10 w-full sm:w-44"
                    aria-label={t(
                      "operations.workspace_access_policy.mode_label",
                      {
                        workspace: workspaceLabel(workspace, t),
                      }
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      [
                        "unrestricted",
                        "restricted",
                        "disabled",
                      ] as WorkspaceAccessMode[]
                    ).map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {t(
                          `operations.workspace_access_policy.modes.${mode}.label`
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <CollapsibleContent>
                {rule.mode === "disabled" ? (
                  <p className="border-t px-4 py-4 text-sm text-muted-foreground">
                    {t("operations.workspace_access_policy.disabled_hint")}
                  </p>
                ) : (
                  <div className="grid gap-4 border-t p-4 xl:grid-cols-[minmax(14rem,0.75fr)_minmax(22rem,1.25fr)]">
                    <fieldset>
                      <legend className="text-sm font-semibold">
                        {t("operations.workspace_access_policy.roles_title")}
                      </legend>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {t(
                          rule.mode === "restricted"
                            ? "operations.workspace_access_policy.roles_restricted_hint"
                            : "operations.workspace_access_policy.roles_unrestricted_hint"
                        )}
                      </p>
                      <div className="mt-3 space-y-1">
                        {ROLE_ORDER.map((role) => {
                          const checked =
                            rule.mode === "restricted"
                              ? rule.allowed_roles.includes(role)
                              : !rule.denied_roles.includes(role);
                          const id = `workspace-${workspace}-${role}`;
                          return (
                            <label
                              key={role}
                              htmlFor={id}
                              className="flex min-h-10 items-center gap-3 rounded-md px-2 hover:bg-muted"
                            >
                              <Checkbox
                                id={id}
                                checked={checked}
                                disabled={mutation.isPending}
                                onCheckedChange={(value) => {
                                  const nextChecked = value === true;
                                  if (rule.mode === "restricted") {
                                    updateRule(workspace, {
                                      allowed_roles: nextChecked
                                        ? Array.from(
                                            new Set([
                                              ...rule.allowed_roles,
                                              role,
                                            ])
                                          )
                                        : rule.allowed_roles.filter(
                                            (item) => item !== role
                                          ),
                                    });
                                  } else {
                                    updateRule(workspace, {
                                      denied_roles: nextChecked
                                        ? rule.denied_roles.filter(
                                            (item) => item !== role
                                          )
                                        : Array.from(
                                            new Set([
                                              ...rule.denied_roles,
                                              role,
                                            ])
                                          ),
                                    });
                                  }
                                }}
                              />
                              <span className="text-sm font-medium">
                                {t(
                                  `operations.workspace_access_policy.roles.${role}`
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    <fieldset className="min-w-0">
                      <legend className="text-sm font-semibold">
                        {t("operations.workspace_access_policy.users_title")}
                      </legend>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {t("operations.workspace_access_policy.users_hint")}
                      </p>
                      <Label
                        htmlFor={`workspace-search-${workspace}`}
                        className="sr-only"
                      >
                        {t("operations.workspace_access_policy.search_label")}
                      </Label>
                      <div className="relative mt-3">
                        <Search
                          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <Input
                          id={`workspace-search-${workspace}`}
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder={t(
                            "operations.workspace_access_policy.search_placeholder"
                          )}
                          className="pl-9"
                        />
                      </div>
                      <ScrollArea className="mt-2 h-56 rounded-md border">
                        <div className="divide-y p-1">
                          {filteredMembers.map((member) => (
                            <WorkspaceMemberOverride
                              key={member.id}
                              workspace={workspace}
                              member={member}
                              rule={rule}
                              disabled={mutation.isPending}
                              onChange={(next) => updateRule(workspace, next)}
                              t={t}
                            />
                          ))}
                          {filteredMembers.length === 0 && (
                            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                              {t("operations.workspace_access_policy.no_users")}
                            </p>
                          )}
                        </div>
                      </ScrollArea>
                    </fieldset>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          );
        })}

        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {t("operations.workspace_access_policy.route_hint")}
          </p>
          <Button
            type="button"
            disabled={!hasChanges || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && (
              <Loader2
                className="mr-2 size-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {t("operations.workspace_access_policy.save")}
          </Button>
        </div>

        {mutation.isError && (
          <p
            role="alert"
            className="rounded-md border border-destructive p-3 text-xs text-destructive"
          >
            {t("operations.workspace_access_policy.save_error")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function WorkspaceMemberOverride({
  workspace,
  member,
  rule,
  disabled,
  onChange,
  t,
}: {
  workspace: WorkspaceKey;
  member: WorkspaceAccessProjectMember;
  rule: WorkspaceAccessRule;
  disabled: boolean;
  onChange: (patch: Partial<WorkspaceAccessRule>) => void;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const value = rule.denied_user_ids.includes(member.id)
    ? "deny"
    : rule.allowed_user_ids.includes(member.id)
      ? "allow"
      : "inherit";

  return (
    <div className="flex min-h-14 items-center gap-3 rounded-md px-2">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">
          {member.name}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {member.email}
        </span>
      </span>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => {
          onChange({
            allowed_user_ids:
              next === "allow"
                ? Array.from(new Set([...rule.allowed_user_ids, member.id]))
                : rule.allowed_user_ids.filter((id) => id !== member.id),
            denied_user_ids:
              next === "deny"
                ? Array.from(new Set([...rule.denied_user_ids, member.id]))
                : rule.denied_user_ids.filter((id) => id !== member.id),
          });
        }}
      >
        <SelectTrigger
          className="h-9 w-32 text-xs"
          aria-label={t(
            "operations.workspace_access_policy.user_override_label",
            {
              user: member.name,
              workspace: workspaceLabel(workspace, t),
            }
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="inherit">
            {t("operations.workspace_access_policy.user_inherit")}
          </SelectItem>
          <SelectItem value="allow">
            {t("operations.workspace_access_policy.user_allow")}
          </SelectItem>
          <SelectItem value="deny">
            {t("operations.workspace_access_policy.user_deny")}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
